import type { PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEventListener } from "@/hooks";
import {
    MINT_EVENTS,
    LOCK_EVENTS,
    DEPOSIT_EVENTS,
    WITHDRAW_EVENTS,
    STAKE_EVENTS,
    type WithdrawSuccessPayload,
    type DepositSuccessPayload,
    type RedeemSuccessPayload,
    REDEEM_EVENTS,
    type StakeSuccessPayload,
    CLAIM_EVENTS,
    type ClaimSuccessPayload,
} from "@/events";
import { QUERY_KEYS } from "@/config";
import { waitForTransactionConfirm } from "@/utils";
import type { MintSuccessPayload, LockSuccessPayload } from "@/events";
import { useAccount } from "wagmi";
import { usePortfolioStore } from "@/zustand/PortfolioStore";

export function CacheInvalidationManager({ children }: PropsWithChildren) {
    const queryClient = useQueryClient();

    const { address } = useAccount();
    const resetPortfolioSelection = usePortfolioStore((state) => state.resetSelection);
    const previousAddressRef = useRef(address);

    useEffect(() => {
        if (previousAddressRef.current !== address) {
            resetPortfolioSelection();
            previousAddressRef.current = address;
        }
    }, [address, resetPortfolioSelection]);

    useEventListener(MINT_EVENTS.SUCCESS, async ({ transactionHash }: MintSuccessPayload) => {
        await waitForTransactionConfirm(transactionHash);
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
        });
    });

    useEventListener(LOCK_EVENTS.SUCCESS, async ({ transactionHash }: LockSuccessPayload) => {
        await waitForTransactionConfirm(transactionHash);
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
        });
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.POSITIONS.LOCK.LIST(address),
        });
    });

    useEventListener(DEPOSIT_EVENTS.SUCCESS, async ({ transactionHash }: DepositSuccessPayload) => {
        await waitForTransactionConfirm(transactionHash);
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
        });
    });

    useEventListener(WITHDRAW_EVENTS.SUCCESS, async ({ transactionHash }: WithdrawSuccessPayload) => {
        await waitForTransactionConfirm(transactionHash);
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
        });
    });

    useEventListener(REDEEM_EVENTS.SUCCESS, async ({ transactionHash }: RedeemSuccessPayload) => {
        await waitForTransactionConfirm(transactionHash);
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
        });
    });

    useEventListener(STAKE_EVENTS.SUCCESS, async ({ transactionHash }: StakeSuccessPayload) => {
        await waitForTransactionConfirm(transactionHash);
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
        });
    });

    useEventListener(CLAIM_EVENTS.SUCCESS, async ({ transactionHash }: ClaimSuccessPayload) => {
        await waitForTransactionConfirm(transactionHash);
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
        });
    });

    return <>{children}</>;
}
